import { Link } from "react-router-dom";
import { USE_CASES } from "../lib/useCases";
import { SIGNUP_URL, SIGNIN_URL, DO_NOT_SELL_URL } from "../lib/config";
import logoWhite from "@assets/meriana_logo_white.png";
import { trackSignupCta } from "../lib/analytics";

export default function Footer() {
  return (
    <footer className="bg-brand-950 text-gray-400">
      {/* Brand showcase */}
      <div className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-16 flex flex-col items-center text-center">
          <img
            src={logoWhite}
            alt="Meriana"
            className="block w-full max-w-[240px]"
            width={557}
            height={600}
            loading="lazy"
          />
          <p className="mt-8 font-display text-white text-2xl sm:text-3xl tracking-tight">
            Your world, distilled.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16 grid grid-cols-1 sm:grid-cols-3 gap-10">
        {/* Brand */}
        <div>
          <p className="font-display text-white text-lg mb-2">Meriana</p>
          <p className="text-sm leading-relaxed">
            AI-synthesized news briefings, delivered on your schedule.
          </p>
          <div className="mt-5 flex items-center gap-4">
            <a
              href="https://www.facebook.com/profile.php?id=61591055109793"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Meriana on Facebook"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/getmeriana/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Meriana on Instagram"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/getmeriana"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Meriana on LinkedIn"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
            <a
              href="https://www.tiktok.com/@getmeriana"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Meriana on TikTok"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Use cases */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
            Use cases
          </p>
          <ul className="space-y-2">
            {USE_CASES.map((uc) => (
              <li key={uc.href}>
                <Link
                  to={uc.href}
                  className="text-sm hover:text-white transition-colors"
                >
                  {uc.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Product */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
            Product
          </p>
          <ul className="space-y-2">
            <li>
              <a
                href={SIGNUP_URL}
                onClick={() => { if (window.location.pathname === "/") trackSignupCta("footer"); }}
                className="text-sm hover:text-white transition-colors"
              >
                Create account
              </a>
            </li>
            <li>
              <a
                href={SIGNIN_URL}
                className="text-sm hover:text-white transition-colors"
              >
                Sign in
              </a>
            </li>
            <li>
              <Link
                to="/blog/"
                className="text-sm hover:text-white transition-colors"
              >
                Blog
              </Link>
            </li>
            <li>
              <Link
                to="/pricing/"
                className="text-sm hover:text-white transition-colors"
              >
                Pricing
              </Link>
            </li>
            <li>
              <Link
                to="/faq/"
                className="text-sm hover:text-white transition-colors"
              >
                FAQ
              </Link>
            </li>
            <li>
              <Link
                to="/frontpage/"
                className="text-sm hover:text-white transition-colors"
              >
                Frontpage — Daily News Puzzle
              </Link>
            </li>
            <li>
              <Link
                to="/games/"
                className="text-sm hover:text-white transition-colors"
              >
                Games — Frontpage, Stacked &amp; Molten
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} Meriana. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy/"
              className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms/"
              className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
            >
              Terms of Service
            </Link>
            <a
              href={DO_NOT_SELL_URL}
              className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
            >
              Do Not Sell or Share My Personal Information
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
